<template>
    <div>
        <!-- Main Dialog for Adding Position -->
        <Dialog
            v-model:visible="openAddPositionDialog"
            modal
            header="Ajouter une position"
            :style="{ width: '30rem' }"
            @hide="resetForm"
        >
            <section>
                <FormGeolocation
                    :index="0"
                    :number="1"
                    :reference="geolocationReference"
                    :optionsCountry="countryOptions"
                    @submit="addGeolocation"
                    @cancel="closeAddDialog"
                />
            </section>
            <template #footer>
                <div class="flex justify-end gap-2">
                    <Button
                        label="Annuler"
                        @click="closeAddDialog"
                        class="button-cancel"
                    />
                    <Button
                        label="Ajouter"
                        @click="submitAddForm"
                        class="p-button-primary"
                        :loading="addLoading"
                    />
                </div>
            </template>
        </Dialog>

        <!-- Edit Dialog -->
        <Dialog
            v-model:visible="editDialogVisible"
            modal
            header="Modifier la position"
            :style="{ width: '30rem' }"
            @hide="resetForm"
        >
            <section>
                <FormGeolocation
                    :index="0"
                    :number="1"
                    :reference="geolocationReference"
                    :optionsCountry="countryOptions"
                    @submit="updateGeolocation"
                    @cancel="closeEditDialog"
                    :isEditMode="true"
                />
            </section>
            <template #footer>
                <div class="flex justify-end gap-2">
                    <Button
                        label="Annuler"
                        @click="closeEditDialog"
                        class="button-cancel"
                    />
                    <Button
                        label="Editer"
                        @click="submitEditForm"
                        class="button-connfirm"
                        :loading="editLoading"
                    />
                </div>
            </template>
        </Dialog>

        <!-- Confirmation Dialog for Deletion -->
        <Dialog
            v-model:visible="deleteDialogVisible"
            modal
            header="Confirmation de suppression"
            :style="{ width: '25rem' }"
        >
            <span class="text-surface-500 dark:text-surface-400 block mb-8">
                Êtes-vous sûr de vouloir supprimer cette géolocalisation ?
            </span>
            <div class="flex justify-end gap-2">
                <Button
                    label="Non"
                    @click="deleteDialogVisible = false"
                    class="button-cancel"
                />
                <Button
                    label="Oui"
                    @click="confirmDelete"
                    class="button-connfirm"
                    :loading="deleteLoading"
                />
            </div>
        </Dialog>

        <!-- Main Dialog for Import multiple addresses csv file-->
        <Dialog
            v-model:visible="openImportFileDialog"
            modal
            header="Importer un fichier CSV d'adresses"
            :style="{ width: '30rem' }"
            @hide="resetForm"
        >
            <FormKit
                type="form"
                :actions="false"
                incomplete-message="Désolé, tous les champs ne sont pas remplis correctement."
                @submit="submitUploadFileAddresses"
            >
            <section>

                    <FormKit
                        type="file"
                        label="fichier csv*"
                        help="Sélectionnez le fichier à uploader."
                        accept=".csv"
                        placeholder="Pas de fichier sélectionné."
                        validation="required|validateFileExtension:csv"
                        :validation-messages="{
              required: 'Aucun fichier sélectionné.',
              validateFileExtension: 'Le fichier doit être au format CSV.'
            }"
                        name="file"
                    />
                    <FormKit
                        type="select"
                        label="Pays*"
                        name="country_code"
                        v-model="selectedImportCountry"
                        :options="countryOptions"
                        help="Sélectionnez le pays pour les adresses importées."
                    />
                <div class="flex justify-end gap-2">
                    <Button
                        label="Annuler"
                        @click="closeImportFileDialog"
                        class="button-cancel"
                    />
                    <Button
                        label="Importer"
                        type="submit"
                        class="p-button-primary"
                        :loading="addLoading"
                    />
                </div>

            </section>
            </FormKit>
            <div>
                <a href="#" @click="downloadCsvTemplate" style="text-decoration: underline">Modèle du fichier CSV</a>
            </div>

        </Dialog>

        <div class="flex justify-between items-center mb-4">
            <h1 class="page-title">La liste des positions de <b>{{ productName }}</b></h1>
            <div class="flex gap-2">
                <Button
                    label="Ajouter une position"
                    @click="() => { resetFormBeforeShow(); openAddPositionDialog = true; }"
                    icon="pi pi-plus"
                    aria-label="Ouvrir le formulaire d'ajout de position"
                    class="button-add"
                />
                <Button
                    label="Importer un fichier CSV d'adresses"
                    @click="() => { openImportFileDialog = true }"
                    icon="pi pi-plus"
                    aria-label="Ouvrir le formulaire pour importer un fichier CSV"
                    class="button-add"
                />
            </div>
        </div>
        <p class="flex justify-end w-full"> Nombre total de géolocalisations : <b>{{ totalItems }}</b></p>

        <DataTable
            :key="tableKey"
            :value="geolocations"
            paginator
            :rows="rowsPerPage"
            :totalRecords="totalItems"
            :loading="loading"
            :lazy="true"
            @page="onPageChange"
            dataKey="id"
            :globalFilterFields="['id', 'formattedAddress', 'latitude', 'longitude']"
            stripedRows
            tableStyle="min-width: 50rem"
            resizableColumns
            columnResizeMode="fit"
            showGridlines
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            :rowsPerPageOptions="[10, 20, 30, 50, 100]"
            :first="(currentPage - 1) * rowsPerPage"
            alwaysShowPaginator
        >
            <template #header>
                <div class="flex justify-between items-center w-full">
                    <span class="p-input-icon-left relative">
                        <i class="pi pi-search" />
                        <InputText
                            class="p-4 w-[300px]"
                            v-model="geolocationFilters.global.value"
                            @update:modelValue="(val) => handleColumnSearch('search', val)"
                            @keyup.enter="handleGlobalSearchImmediate"
                            placeholder="Rechercher dans tout le tableau"
                        />

                    </span>

                    <div class="flex items-center gap-2">
                        <Button
                            v-if="isApiFiltered"
                            label="Réinitialiser la recherche"
                            icon="pi pi-refresh"
                            @click="resetApiSearch"
                            class="p-button-outlined p-button-sm"
                        />

                        <span v-if="loading" class="flex items-center">
                            <ProgressSpinner style="width: 20px; height: 20px" />
                            <span class="ml-2">Chargement...</span>
                        </span>
                    </div>
                </div>
            </template>

            <template #empty>
                <div class="text-center py-4">
                    <i class="pi pi-map-marker text-4xl text-gray-400 mb-2" />
                    <p>Aucune position trouvée</p>
                    <Button
                        label="Ajouter une position"
                        @click="() => { resetFormBeforeShow(); openAddPositionDialog = true; }"
                        class="mt-2 button-add"
                    />
                </div>
            </template>

            <Column header="#" class="max-w-[30px]">
                <template #body="{ index }">
                    {{ (currentPage - 1) * rowsPerPage + index + 1 }}
                </template>
            </Column>

            <Column field="id" header="Id"  class="max-w-[70px]" />

            <Column field="latitude" header="Latitude"  class="max-w-[100px]">
                <template #header>
                    <div class="column-header-with-search geo-col w-full">
                        <div class="column-search-input">
                            <InputText
                                class="w-full p-2 min-w-[100px]"
                                v-model="searchLatitude"
                                @update:modelValue="(val) => handleColumnSearch('latitude', val)"
                                @keyup.enter="searchGeolocationsByFilter('latitude', searchLatitude)"
                                placeholder="Latitude"
                            />
                        </div>
                    </div>
                </template>
            </Column>

            <Column field="longitude" header="Longitude"  class="max-w-[100px]">
                <template #header>
                    <div class="column-header-with-search geo-col w-full">
                        <div class="column-search-input">
                            <InputText
                                class="w-full p-2 min-w-[100px]"
                                v-model="searchLongitude"
                                @update:modelValue="(val) => handleColumnSearch('longitude', val)"
                                @keyup.enter="searchGeolocationsByFilter('longitude', searchLongitude)"
                                placeholder="Longitude"
                            />
                        </div>
                    </div>
                </template>
            </Column>

            <Column field="formattedAddress" header="Adresse"  class="min-w-[100px]">
                <template #header>
                    <div class="column-header-with-search geo-col w-full">
                        <div class="column-search-input">
                            <InputText
                                class="w-full p-2"
                                v-model="searchAddress"
                                @update:modelValue="(val) => handleColumnSearch('formattedAddress', val)"
                                @keyup.enter="searchGeolocationsByFilter('formattedAddress', searchAddress)"
                                placeholder="Adresse"
                            />
                        </div>
                    </div>
                </template>
                <template #body="{ data }">
                    <span class="block max-w-[200px]">{{ (data.longitude && data.longitude)  ? data.formattedAddress : 'Adresse vide ou géolocalisation désactivée'}}</span>
                </template>
            </Column>

            <Column field="country_code" header="Pays"  class="max-w-[100px]">
                <template #header>
                    <div class="column-header-with-search geo-col w-full">
                        <div class="column-search-input">
                            <InputText
                                class="w-full p-2"
                                v-model="searchCountry"
                                @update:modelValue="(val) => handleColumnSearch('country_code', val)"
                                @keyup.enter="searchGeolocationsByFilter('country_code', searchCountry)"
                                placeholder="Pays"
                            />
                        </div>
                    </div>
                </template>
                <template #body="{ data }">
                    <span>{{ data.country_code ? countryOptions[data.country_code] || data.country_code : '' }}</span>
                </template>
            </Column>

            <Column header="Actions" class="whitespace-nowrap max-w-[80px]">
                <template #body="{ data }" >
                    <div class="flex gap-2" v-if="data.longitude && data.longitude">
                        <Button
                            size="small"
                            icon="pi pi-pencil"
                            class="p-edition"
                            style="margin-right: 0.5em"
                            title="Modifier"
                            @click="openEditDialog(data)"
                        />
                        <Button
                            size="small"
                            icon="pi pi-trash"
                            class="p-button-danger"
                            style="margin-right: 0.5em"
                            title="Supprimer"
                            @click="openDeleteDialog(data.uuid)"
                            :loading="deleteLoading && selectedGeolocationUuid === data.uuid"
                        />
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { FilterMatchMode } from 'primevue/api';
import { deleteGeolocation, getOptionsCountry, getCountryCodeByName } from '@/_services/geolocation/country';
import { productService } from '@/_services/product/product';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import ProgressSpinner from 'primevue/progressspinner';
import FormGeolocation from "@/components/forms/product/FormGeolocationEdition.vue";
import { debounce } from 'lodash-es';
import axios from 'axios';
import { useGeolocationStore } from '@/stores/geoloc/geolocationStore';
import Tooltip from 'primevue/tooltip';
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";

interface Geolocation {
    uuid: string;
    id: string;
    latitude: number;
    longitude: number;
    formattedAddress: string;
    address?: string;
    zipCode?: string;
    city?: string;
    country_code?: string;
}

interface GeolocationReference {
    uuid?: string;
    manualMethod: string;
    address: string | null;
    zipCode: string | null;
    city: string | null;
    country_code: string;
    latitude: string | number | null;
    longitude: string | number | null;
    formattedAddress: string | null;
}

const route = useRoute();
const router = useRouter();
const toast = useToast();
const loading = ref(false);
const deleteLoading = ref(false);
const addLoading = ref(false);
const editLoading = ref(false);
const deleteDialogVisible = ref(false);
const openAddPositionDialog = ref(false);
const editDialogVisible = ref(false);
const selectedGeolocationUuid = ref<string | null>(null);
const selectedGeolocation = ref<Geolocation | null>(null);
const geolocations = ref<Geolocation[]>([]);
const productName = ref<string>('');
const geolocationStore = useGeolocationStore();
const isApiFiltered = ref(false);

// Pagination variables
const rowsPerPage = ref(30);
const currentPage = ref(1);

// Search variables
const searchId = ref('');
const searchLatitude = ref('');
const searchLongitude = ref('');
const searchAddress = ref('');
const searchCountry = ref('');
const totalItems = ref(0);

// Variables pour suivre la recherche en cours (pour la pagination)
const currentSearchField = ref('');
const currentSearchValue = ref('');

const tableKey = ref(0);
const openImportFileDialog = ref(false);
const selectedImportCountry = ref('FR');

const geolocationReference = ref<GeolocationReference>({
    uuid: null,
    manualMethod: 'address',
    address: null,
    zipCode: null,
    city: null,
    country_code: 'FR',
    latitude: null,
    longitude: null,
    formattedAddress: null
});

const countryOptions = ref(getOptionsCountry());

const geolocationFilters = ref({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    latitude: { value: null, matchMode: FilterMatchMode.CONTAINS },
    longitude: { value: null, matchMode: FilterMatchMode.CONTAINS },
    formattedAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    country_code: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const fetchProductData = async (   
    techUuid: string,
    page = 1,
    itemsPerPage = rowsPerPage.value
) => {
    loading.value = true;
    try {
        if (!techUuid || typeof techUuid !== 'string') {
            throw new Error('UUID technique non valide');
        }

        const params = {
            page: String(page),
            itemsPerPage: String(itemsPerPage)
        };

        const response = await productService.productLocations(techUuid, params);

        if (response) {
            if ('items' in response && Array.isArray(response.items)) {
                geolocations.value = response.items;
                totalItems.value = response.totalItems || 0;
                currentPage.value = page;
                rowsPerPage.value = itemsPerPage;

                if (response.items.length === 0 && totalItems.value > 0 && page > 1) {
                    return fetchProductData(techUuid, 1, itemsPerPage);
                }

                forceTableRefresh();
            } else {
                geolocations.value = [];
                totalItems.value = 0;
                toast.add({
                    severity: 'warn',
                    summary: 'Avertissement',
                    detail: 'Format de réponse API inattendu',
                    life: 5000,
                });
            }
        } else {
            geolocations.value = [];
            totalItems.value = 0;
            productName.value = 'Produit non trouvé';
        }
    } catch (error) {
        let errorMessage = 'Impossible de charger les données du produit';

        if (error.response) {
            if (error.response.status === 404) {
                errorMessage = 'Produit non trouvé ou pas de géolocalisations disponibles';
            } else if (error.response.status === 401 || error.response.status === 403) {
                errorMessage = 'Vous n\'avez pas les droits d\'accès nécessaires';
            }
        }

        toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: errorMessage,
            life: 5000,
        });

        geolocations.value = [];
        totalItems.value = 0;
    } finally {
        loading.value = false;
    }
};

const forceTableRefresh = () => {
    tableKey.value++;
};

const onPageChange = (event: any) => {
    const newPage = event.page + 1;
    const itemsPerPage = event.rows;

    const rowsChanged = itemsPerPage !== rowsPerPage.value;

    const techUuid = getTechnicalUuid();

    rowsPerPage.value = itemsPerPage;

    if (rowsChanged) {
        currentPage.value = 1;
        forceTableRefresh();
    } else {
        currentPage.value = newPage;
    }

    if (isApiFiltered.value) {
        loading.value = true;

        let searchField = 'search';
        let searchValue = '';

        if (geolocationFilters.value.global.value) {
            searchField = 'search';
            searchValue = geolocationFilters.value.global.value;
        } else if (searchLatitude.value) {
            searchField = 'latitude';
            searchValue = searchLatitude.value;
        } else if (searchLongitude.value) {
            searchField = 'longitude';
            searchValue = searchLongitude.value;
        } else if (searchAddress.value) {
            searchField = 'formattedAddress';
            searchValue = searchAddress.value;
        }

        if (searchValue && searchValue.trim() !== '') {
            const paginationParams = {
                page: String(currentPage.value),
                perPage: String(itemsPerPage)
            };

            productService.searchGeolocations(
                techUuid,
                searchField,
                searchValue.trim(),
                paginationParams
            ).then(response => {
                let items = [];
                let total = 0;

                if (response) {
                    if (response['hydra:member'] && Array.isArray(response['hydra:member'])) {
                        items = response['hydra:member'];
                        total = response['hydra:totalItems'] || 0;
                    } else if (response.items && Array.isArray(response.items)) {
                        items = response.items;
                        total = response.totalItems || 0;
                    } else if (response.data && response.data['hydra:member'] && Array.isArray(response.data['hydra:member'])) {
                        items = response.data['hydra:member'];
                        total = response.data['hydra:totalItems'] || 0;
                    } else if (response.data && Array.isArray(response.data)) {
                        items = response.data;

                        if (response.totalItems !== undefined) {
                            total = response.totalItems;
                        } else if (response.data['hydra:totalItems'] !== undefined) {
                            total = response.data['hydra:totalItems'];
                        } else {
                            total = totalItems.value > 0 ? totalItems.value : items.length;
                        }
                    } else if (Array.isArray(response)) {
                        items = response;
                        total = totalItems.value > 0 ? totalItems.value : items.length;
                    }
                }

                if (items.length > 0) {
                    geolocations.value = items;
                    if (total > 0) {
                        totalItems.value = total;
                    }
                    forceTableRefresh();
                } else {
                    if (totalItems.value > 0 && currentPage.value > 1) {
                        currentPage.value = currentPage.value - 1;
                        onPageChange({ page: currentPage.value - 1, rows: itemsPerPage });
                    } else {
                        geolocations.value = [];
                        totalItems.value = 0;
                        forceTableRefresh();
                    }
                }
            }).catch(error => {
                toast.add({
                    severity: 'error',
                    summary: 'Erreur',
                    detail: 'Impossible de charger la page demandée',
                    life: 3000,
                });
            }).finally(() => {
                loading.value = false;
            });

            return;
        }
    }

    const pageToFetch = rowsChanged ? 1 : currentPage.value;
    fetchProductData(techUuid, pageToFetch, itemsPerPage).then(() => {
        forceTableRefresh();
    });
};

const resetForm = () => {
    geolocationStore.resetForm();
    selectedGeolocation.value = null;
};

const resetFormBeforeShow = () => {
    geolocationStore.resetForm();

    geolocationReference.value = {
        uuid: null,
        manualMethod: 'address',
        address: null,
        zipCode: null,
        city: null,
        country_code: 'FR',
        latitude: null,
        longitude: null,
        formattedAddress: null
    };
};

const openDeleteDialog = (uuid: string) => {
    selectedGeolocationUuid.value = uuid;
    deleteDialogVisible.value = true;
};

const closeAddDialog = () => {
    openAddPositionDialog.value = false;
    resetForm();
};

const closeImportFileDialog = () => {
    openImportFileDialog.value = false;
    selectedImportCountry.value = 'FR';
};

const openEditDialog = (geolocation: Geolocation) => {
    selectedGeolocation.value = geolocation;
    geolocationStore.setEditMode(true);

    let country_code = geolocation.country_code || 'FR';
    country_code = getCountryCodeByName(country_code);

    geolocationStore.initGeolocationForm({
        uuid: geolocation.uuid,
        manualMethod: 'address',
        address: geolocation.address || '',
        zipCode: geolocation.zipCode || '',
        city: geolocation.city || '',
        country_code: country_code,
        latitude: String(geolocation.latitude) || '',
        longitude: String(geolocation.longitude) || '',
        formattedAddress: geolocation.formattedAddress || ''
    });

    geolocationReference.value = {
        uuid: geolocation.uuid,
        manualMethod: 'address',
        address: geolocation.address || '',
        zipCode: geolocation.zipCode || '',
        city: geolocation.city || '',
        country_code: country_code,
        latitude: String(geolocation.latitude) || '',
        longitude: String(geolocation.longitude) || '',
        formattedAddress: geolocation.formattedAddress || ''
    };

    editDialogVisible.value = true;
};

const closeEditDialog = () => {
    editDialogVisible.value = false;
    selectedGeolocation.value = null;
    geolocationStore.setEditMode(false);
    resetForm();
};

const addGeolocation = async (payload: any) => {
    addLoading.value = true;
    try {
        const productUuid = getTechnicalUuid();
        if (!productUuid) {
            throw new Error('UUID du produit non trouvé');
        }

        await axios.post(`/admin/geolocation-products`, {
            ...payload,
            uuid: productUuid
        });

        await fetchProductData(productUuid);

        toast.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'La géolocalisation a été ajoutée avec succès',
            life: 3000,
        });

        closeAddDialog();
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.response?.data?.message || error.message || 'Une erreur est survenue lors de l\'ajout',
            life: 5000,
        });
    } finally {
        addLoading.value = false;
    }
};

const updateGeolocation = async (formData: any) => {
    if (!selectedGeolocation.value) return;

    loading.value = true;
    try {
        await fetchProductData(route.params.uuid as string);
        toast.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'La géolocalisation a été modifiée avec succès',
            life: 3000,
        });
        closeEditDialog();
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.message || 'Une erreur est survenue lors de la modification',
            life: 3000,
        });
    } finally {
        loading.value = false;
    }
};

const submitEditForm = async () => {
    editLoading.value = true;
    try {
        if (!selectedGeolocation.value || !geolocationStore.uuid) {
            throw new Error('Données de géolocalisation manquantes');
        }

        const payload = geolocationStore.createPayload();

        payload.country_code = getCountryCodeByName(payload.country_code);

        const geoUuid = geolocationStore.uuid;
        const url = `/admin/geolocation-products/${geoUuid}`;
        await axios.patch(url, payload);

        await fetchProductData(getTechnicalUuid());

        toast.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'La géolocalisation a été mise à jour avec succès',
            life: 3000,
        });

        closeEditDialog();
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.response?.data?.message || error.message || 'Une erreur est survenue lors de la mise à jour',
            life: 5000,
        });
    } finally {
        editLoading.value = false;
    }
};

const confirmDelete = async () => {
    if (!selectedGeolocationUuid.value) return;

    deleteLoading.value = true;
    try {
        await deleteGeolocation(selectedGeolocationUuid.value);

        const techUuid = localStorage.getItem('technicalVariantUuid');
        if (!techUuid) {
            throw new Error('UUID technique non trouvé');
        }

        await fetchProductData(techUuid);

        toast.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'La géolocalisation a été supprimée avec succès',
            life: 3000,
        });
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.message || 'Une erreur est survenue lors de la suppression',
            life: 3000,
        });
    } finally {
        deleteLoading.value = false;
        deleteDialogVisible.value = false;
        selectedGeolocationUuid.value = null;
    }
};

const searchGeolocationsByFilter = async (field: string, value: string) => {
    if (!value || value.trim() === '') {
        if (isApiFiltered.value) {
            return resetApiSearch();
        }
        return;
    }

    try {
        loading.value = true;
        isApiFiltered.value = true;
        currentPage.value = 1;

        const productUuid = getTechnicalUuid();

        const paginationParams = {
            page: String(currentPage.value),
            itemsPerPage: String(rowsPerPage.value)
        };

        let searchField = field;
        let searchValue = value.trim();

        let response;

        if (productService.searchGeolocations) {
            response = await productService.searchGeolocations(
                productUuid,
                searchField,
                searchValue,
                paginationParams
            );
        } else {
            const endpoint = `/admin/product/${productUuid}/geolocation`;
            const params: Record<string, string> = {
                ...paginationParams
            };
            params[searchField] = searchValue;

            const url = buildApiUrl(endpoint, params);
            const axiosResponse = await axios.get(url);
            response = axiosResponse.data;
        }

        let items = [];
        let total = 0;

        if (response) {
            if (response['hydra:member'] && Array.isArray(response['hydra:member'])) {
                items = response['hydra:member'];
                total = response['hydra:totalItems'] || 0;
            }
            else if (response.items && Array.isArray(response.items)) {
                items = response.items;
                total = response.totalItems || 0;
            }
            else if (response.data && response.data['hydra:member'] && Array.isArray(response.data['hydra:member'])) {
                items = response.data['hydra:member'];
                total = response.data['hydra:totalItems'] || 0;
            }
            else if (response.data && Array.isArray(response.data)) {
                items = response.data;

                if (response.totalItems !== undefined) {
                    total = response.totalItems;
                } else if (response.data['hydra:totalItems'] !== undefined) {
                    total = response.data['hydra:totalItems'];
                } else {
                    total = totalItems.value > 0 ? totalItems.value : items.length;
                }
            }
            else if (Array.isArray(response)) {
                items = response;
                total = totalItems.value > 0 ? totalItems.value : items.length;
            }
        }

        if (items.length > 0) {
            geolocations.value = items;
            totalItems.value = total;
            forceTableRefresh();

            toast.add({
                severity: 'info',
                summary: 'Recherche',
                detail: `${total} résultat(s) trouvé(s)`,
                life: 3000,
            });
        } else {
            geolocations.value = [];
            totalItems.value = 0;
            forceTableRefresh();

            toast.add({
                severity: 'info',
                summary: 'Recherche',
                detail: 'Aucun résultat trouvé',
                life: 3000,
            });
        }
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Erreur de recherche',
            detail: error.response?.data?.message || error.message || 'Erreur lors de la recherche',
            life: 3000,
        });

        geolocations.value = [];
        totalItems.value = 0;
        forceTableRefresh();
    } finally {
        loading.value = false;
    }
};

const resetApiSearch = async () => {
    loading.value = true;

    try {
        searchId.value = '';
        searchLatitude.value = '';
        searchLongitude.value = '';
        searchAddress.value = '';
        searchCountry.value = '';

        geolocationFilters.value.formattedAddress.value = null;
        geolocationFilters.value.latitude.value = null;
        geolocationFilters.value.longitude.value = null;
        geolocationFilters.value.country_code.value = null;
        geolocationFilters.value.global.value = null;

        currentSearchField.value = '';
        currentSearchValue.value = '';
        isApiFiltered.value = false;

        currentPage.value = 1;

        const techUuid = getTechnicalUuid();
        await fetchProductData(techUuid, 1);

        forceTableRefresh();

        toast.add({
            severity: 'info',
            summary: 'Recherche réinitialisée',
            detail: 'Toutes les données ont été rechargées',
            life: 3000,
        });
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.message || 'Une erreur est survenue lors de la réinitialisation',
            life: 3000,
        });
    } finally {
        loading.value = false;
    }
};

watch([
    () => geolocationFilters.value.global.value,
    searchLatitude,
    searchLongitude,
    searchAddress,
    currentPage,
    rowsPerPage
], () => {
}, { deep: true });

const buildApiUrl = (endpoint: string, params?: Record<string, string>) => {
    let url = endpoint;
    if (params && Object.keys(params).length > 0) {
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                queryParams.append(key, value);
            }
        });

        const queryString = queryParams.toString();
        if (queryString) {
            url += (url.includes('?') ? '&' : '?') + queryString;
        }
    }

    return url;
};

const previousSearchLatitude = ref('');
const previousSearchLongitude = ref('');
const previousSearchAddress = ref('');
const previousSearchCountry = ref('');
const previousGlobalSearch = ref('');

const handleColumnSearch = (field: string, value: string) => {
    let previousValue = '';

    switch (field) {
        case 'latitude':
            previousValue = previousSearchLatitude.value;
            previousSearchLatitude.value = value;
            break;
        case 'longitude':
            previousValue = previousSearchLongitude.value;
            previousSearchLongitude.value = value;
            break;
        case 'formattedAddress':
            previousValue = previousSearchAddress.value;
            previousSearchAddress.value = value;
            break;
        case 'country_code':
            previousValue = previousSearchCountry.value;
            previousSearchCountry.value = value;
            break;
        case 'search':
            previousValue = previousGlobalSearch.value;
            previousGlobalSearch.value = value;
            break;
    }

    if (value !== previousValue) {
        if (value && value.trim() !== '' && value.trim().length >= 2) {
            debounceApiSearch(field, value, previousValue);
        } else if (isApiFiltered.value && previousValue && !value) {
            resetApiSearch();
        }
    }
};

const debounceApiSearch = debounce((field: string, value: string, previousValue: string) => {
    searchGeolocationsByFilter(field, value.trim());
}, 500);

const handleGlobalSearchImmediate = () => {
    const searchValue = geolocationFilters.value.global.value;

    if (searchValue && searchValue.trim() !== '') {
        searchGeolocationsByFilter('search', searchValue.trim());
    } else if (isApiFiltered.value) {
        resetApiSearch();
    }
};

const validateSearchResponse = (response: any) => {
    let total = 0;

    if (response) {
        if (response['hydra:totalItems'] !== undefined) {
            total = response['hydra:totalItems'];
        } else if (response.totalItems !== undefined) {
            total = response.totalItems;
        } else if (response.data && response.data['hydra:totalItems'] !== undefined) {
            total = response.data['hydra:totalItems'];
        }
    }
    return total;
};

const getTechnicalUuid = (): string => {
    const techUuid = route.params.uuid;
    if (!techUuid || typeof techUuid !== 'string') {
        throw new Error('UUID technique non trouvé dans l\'URL');
    }
    return techUuid;
};

const submitAddForm = async () => {
    const formData = geolocationStore.createPayload();

    const payload = {
        uuid: formData.uuid || undefined,
        address: formData.address || null,
        city: formData.city || null,
        zipCode: formData.zipCode || null,
        country_code: formData.country_code,
        formattedAddress: formData.formattedAddress || null,
        latitude: typeof formData.latitude === 'string' ? parseFloat(formData.latitude) : formData.latitude,
        longitude: typeof formData.longitude === 'string' ? parseFloat(formData.longitude) : formData.longitude
    };

    await addGeolocation(payload);
};

onMounted(() => {
    try {
        const techUuid = getTechnicalUuid();
        fetchProductData(techUuid);
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.message || 'UUID technique non trouvé',
            life: 3000,
        });
    }
});

function submitUploadFileAddresses(data) {
    openImportFileDialog.value = false;
    const form = new FormData();
    form.append('file', data.file[0].file);
    form.append('variantProduct', '/api/variant-product/' + getTechnicalUuid());
    form.append('countryCode', selectedImportCountry.value);
    swal.success("L'import des adresses est en cours","");
    return ApiService.post('product_geoloc_imports', form)
        .then(({data}) => {
             fetchProductData(getTechnicalUuid());
            toast.add({
                severity: 'success',
                summary: 'Succès',
                detail: "L'import des adresses est terminé avec succès. Vous recevrez un mail recap.",
                life: 3000,
            });
        })
        .catch(({response}) => {
            swal.error('Fichier au mauvais format');
        });
}

function downloadCsvTemplate() {
    const headers = ['Adresse', 'Code Postal', 'Ville'];
    const rows = [
        ['5 Rue Feydeau', '75002', 'Paris'],
        ['6 Rue Feydeau', '75002', 'Paris'],
        ['7 Rue Feydeau', '75002', 'Paris']
    ];
    let csvContent = headers.join(';') + '\n';
    rows.forEach(row => {
        csvContent += row.join(';') + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'adresses.csv');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

</script>

<style lang="scss">
.geo-col + .p-column-title {
    display: none;
}

.custom-column-headers {
    margin-bottom: 0;
    border: 1px solid #dee2e6;
    border-bottom: none;
    background-color: #f8f9fa;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}

.custom-column-headers .border-bottom {
    border-bottom: 1px solid #dee2e6;
}

.column-title {
    font-weight: bold;
    padding: 0.75rem 0.5rem;
    color: #495057;
    text-align: left;
    font-size: 1rem;
}

.p-datatable .p-datatable-thead > tr > th:first-child {
    border-top-left-radius: 0;
}

.p-datatable .p-datatable-thead > tr > th:last-child {
    border-top-right-radius: 0;
}
</style>
